'use client'

import { ButtonGhost } from '@fukku/button/Ghost'
import type { DialogAPI } from '@fukku/dialog'
import { BottomSheet } from '@fukku/dialog/BottomSheet'
import { Content } from '@fukku/dialog/Content'
import { Header } from '@fukku/dialog/Header'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import { useColors } from 'pdp/src/contexts/ColorsProvider/ColorsProvider'
import type { Color, FullProduct, Look } from 'pdp/src/types/FullProduct'
import { ColorSelectorCounter } from 'product-components/components/ColorSelector/ColorSelectorCounter/ColorSelectorCounter'
import { ProductLocations } from 'product/types/ProductLocations'
import { useRef } from 'react'

import { REDUCED_COLOR_LIST_MAX_LENGTH } from '../ColorsSelector'

const DynamicColorList = dynamic(
	() => import('../ColorList/ColorList').then((mod) => mod.ColorList),
	{
		ssr: false,
	}
)

export interface SheetColorsSelectorProps {
	readonly product: FullProduct
	readonly look: Look
}

export const SheetColorsSelector = ({
	product,
	look,
}: SheetColorsSelectorProps) => {
	const { colors, selectedColor } = useColors()
	const { t } = useLabels()
	const modalRef = useRef<DialogAPI>(null)

	const openSheet = () => {
		modalRef.current?.show()
	}

	const isColorListReducedSelected: boolean = colors
		.slice(0, REDUCED_COLOR_LIST_MAX_LENGTH - 1)
		.some((color: Color) => color.id === selectedColor.id)

	return (
		<>
			<ButtonGhost onClick={openSheet}>
				<ColorSelectorCounter
					data-testid='sheetColorsSelector.label.more'
					selected={!isColorListReducedSelected}
					count={colors.length - REDUCED_COLOR_LIST_MAX_LENGTH + 1}
				/>
			</ButtonGhost>
			<BottomSheet
				ref={modalRef}
				title={t('productCard.selectColor.title')}
				data-testid='colorsSelectorSheet'
			>
				<Header
					title={t('productCard.selectColor.title')}
					subtitle={selectedColor.label}
				/>
				<Content>
					<DynamicColorList
						product={product}
						look={look}
						location={ProductLocations.ColorSelectorSheet}
					/>
				</Content>
			</BottomSheet>
		</>
	)
}
